import { useState } from "react";
import { api } from "../api";
import { ErrorState } from "../ui/State";
import type { Evidence, Packet, Result, StructureResult } from "./xero-history-import-types";

export function XeroSupplierSourceCash({
  packet,
  structure,
  evidence,
  reason,
  root,
  busy,
  setBusy,
  stop,
}: {
  packet: Packet;
  structure: StructureResult | undefined;
  evidence: (checksum: string) => Omit<Evidence, "title">;
  reason: string;
  root: string;
  busy: boolean;
  setBusy: (value: boolean) => void;
  stop: { current: boolean };
}) {
  const groups = packet.supplierSourceCashMappings ?? [],
    [results, setResults] = useState<Record<string, Result>>({}),
    [page, setPage] = useState(0);
  const rows = groups.flatMap((g) =>
    g.payments.map((m) => {
      const cash = packet.history.payments?.find((c) => c.sourceId === m.sourceId),
        invoices = cash?.applications.map((a) => packet.history.invoices.find((i) => i.sourceId === a.invoiceSourceId));
      return {
        group: g,
        m,
        cash,
        invoices,
        dates: invoices?.map((i) => (cash && i ? [cash.date, i.date].sort().at(-1) : undefined)) ?? [],
      };
    }),
  );
  async function copy() {
    if (!structure) return;
    setBusy(true);
    stop.current = false;
    try {
      for (const g of groups) {
        if (stop.current) break;
        if (results[g.bankTransactionId]?.canonicalId) continue;
        try {
          const payments = g.payments.map((m) => {
            const cash = packet.history.payments?.find((c) => c.sourceId === m.sourceId),
              periods = structure.periods.filter((p) => cash && p.startsOn <= cash.date && p.endsOn >= cash.date);
            if (!cash || periods.length !== 1)
              throw Error("Prepare one original full-year period for each original cash date.");
            return { sourceId: m.sourceId, periodId: periods[0]!.id, evidence: evidence(m.sourceChecksum) };
          });
          const originals = (packet.history.payments ?? []).filter((p) =>
            g.payments.some((m) => m.sourceId === p.sourceId),
          );
          const ids = new Set([
            ...g.payments.map((m) => m.sourceId),
            ...originals.flatMap((p) => p.applications.map((a) => a.invoiceSourceId)),
          ]);
          const { checksum, ...witness } = g.bankWitness;
          const result = await api<{ groupId: string }>(root + "/payable-source-cash", {
            method: "POST",
            body: JSON.stringify({
              history: {
                organisation: packet.history.organisation,
                selector: packet.history.selector,
                buildingId: packet.history.buildingId,
                events: packet.history.events.filter((e) => ids.has(e.sourceId)),
                invoices: packet.history.invoices.filter((i) => ids.has(i.sourceId)),
                payments: originals,
                accountMappings: packet.history.accountMappings ?? [],
              },
              scheduleId: structure.scheduleId,
              bankTransactionId: g.bankTransactionId,
              bankWitness: { ...witness, evidence: evidence(checksum) },
              payments,
              projectionPolicy: g.projectionPolicy,
              reason,
            }),
          });
          setResults((r) => ({ ...r, [g.bankTransactionId]: { canonicalId: result.groupId } }));
        } catch (error) {
          setResults((r) => ({ ...r, [g.bankTransactionId]: { error } }));
        }
      }
    } finally {
      setBusy(false);
    }
  }
  return (
    <section aria-label="Original supplier payment dates">
      <h2>Original supplier payment dates</h2>
      <p>
        {rows.length} original payments in {groups.length} bank groups ·{" "}
        {Object.values(results).filter((r) => r.canonicalId).length} groups confirmed in this session.
      </p>
      <p>
        The Xero accounting date and bank date are retained. A payment made before its invoice is applied on the later
        invoice date; that bookkeeping date is shown below.
      </p>
      <button disabled={busy || !structure || reason.trim().length < 5} onClick={() => void copy()}>
        Copy original supplier payments with separate dates
      </button>
      <table>
        <thead>
          <tr>
            <th>Original invoice</th>
            <th>Xero accounting date</th>
            <th>Bank date</th>
            <th>Invoice application date</th>
            <th>Import result</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(page * 25, (page + 1) * 25).map((r) => (
            <tr key={r.m.sourceId}>
              <td>{r.invoices?.map((i) => i?.reference || "Original reference not supplied").join(", ")}</td>
              <td>{r.cash?.date ?? "Unknown"}</td>
              <td>{r.group.bankWitness.statementDate}</td>
              <td>{r.dates.map((d) => d ?? "Unknown").join(", ")}</td>
              <td>
                {results[r.group.bankTransactionId]?.canonicalId ? (
                  "Copied"
                ) : results[r.group.bankTransactionId]?.error ? (
                  <ErrorState error={results[r.group.bankTransactionId]?.error} />
                ) : (
                  "Ready for source checks"
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 25 && (
        <nav aria-label="Original supplier payment date pages">
          <button disabled={!page} onClick={() => setPage(page - 1)}>
            Previous
          </button>{" "}
          Page {page + 1} of {Math.ceil(rows.length / 25)}{" "}
          <button disabled={(page + 1) * 25 >= rows.length} onClick={() => setPage(page + 1)}>
            Next
          </button>
        </nav>
      )}
    </section>
  );
}
